import {
  createContext,
  type Dispatch,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import {
  INITIAL_KICODE_STATE,
  kiCodeReducer,
  type KiCodeAction,
  type KiCodeSessionState,
} from './kiCodeState';
import { useKiCodeAvailability } from './useKiCodeAvailability';
import {
  KiCodeContext,
  KiCodeLauncherContext,
  type KiCodeApi,
  type KiCodeLauncher,
} from './useKiCode';
import { isSameKiCodeTarget, type KiCodeOpenArgs } from './profiles';
import { guardedKiCodeOpen, kiCodeOpenAction } from './launchKiCode';
import { loadKiCodeResume } from './session/resumeStorage';

interface KiCodeSessionHostState {
  session: KiCodeSessionState | null;
  identity: string | null;
  dispatch: Dispatch<KiCodeAction>;
}

// Internal: the full session for KiCodeHost. Public callers use useKiCode.
const KiCodeSessionContext = createContext<KiCodeSessionHostState>({
  session: null,
  identity: null,
  dispatch: () => {},
});

export const useKiCodeSessionHost = () => useContext(KiCodeSessionContext);

export interface KiCodeStateProviderProps {
  children: ReactNode;
}

/**
 * KiCode session state and the public open/close API. Mounted above
 * PluginContextWrapper so plugin host callbacks can open KiCode; it must not
 * depend on PluginContext. KiCodeHost renders the panel below it.
 */
export const KiCodeStateProvider: FC<KiCodeStateProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(kiCodeReducer, INITIAL_KICODE_STATE);

  const { available, identity } = useKiCodeAvailability();
  if (available !== state.available || identity !== state.identity) {
    dispatch({
      type: 'availability',
      available,
      identity,
      resume: available ? loadKiCodeResume(identity) : null,
    });
  }

  const { session } = state;
  const target = session?.target ?? null;
  // Read at call time so `open` keeps one identity across sessions.
  const targetRef = useRef(target);
  useLayoutEffect(() => {
    targetRef.current = target;
  }, [target]);
  // Re-checked when a guarded open finally runs: access can change while a
  // busy surface's prompt is showing, and the reducer then drops the open.
  const availableRef = useRef(state.available);
  useLayoutEffect(() => {
    availableRef.current = state.available;
  }, [state.available]);

  const requestOpen = useCallback(
    (args: KiCodeOpenArgs) =>
      guardedKiCodeOpen(
        kiCodeOpenAction(
          args,
          () => availableRef.current,
          () => dispatch({ type: 'open', args })
        ),
        Boolean(
          targetRef.current && isSameKiCodeTarget(targetRef.current, args)
        )
      ),
    []
  );
  const open = useCallback(
    (args: KiCodeOpenArgs) => {
      void requestOpen(args);
    },
    [requestOpen]
  );
  const close = useCallback(() => dispatch({ type: 'close' }), []);

  const api = useMemo<KiCodeApi>(
    () => ({ open, close, session: target, available: state.available }),
    [open, close, target, state.available]
  );
  const launcher = useMemo<KiCodeLauncher>(
    () => ({ open: requestOpen, available: state.available }),
    [requestOpen, state.available]
  );
  const host = useMemo<KiCodeSessionHostState>(
    () => ({ session, identity: state.identity, dispatch }),
    [session, state.identity]
  );

  return (
    <KiCodeContext.Provider value={api}>
      <KiCodeLauncherContext.Provider value={launcher}>
        <KiCodeSessionContext.Provider value={host}>
          {children}
        </KiCodeSessionContext.Provider>
      </KiCodeLauncherContext.Provider>
    </KiCodeContext.Provider>
  );
};
